/**
 * The schema the shared DictationOverlay renders for REQUEST creation.
 *
 * Sibling of listingDictation.js. The overlay is domain-agnostic by design —
 * "Request creation reuses it by passing a different `schema` and
 * `renderReview`" — so everything domain-shaped lives here and in
 * RequestDictationReview, and the overlay itself is untouched.
 *
 * One difference from the listing schema, and it is the whole reason this file
 * exists rather than a flag in that one: a request can describe SEVERAL watches
 * (up to 3 per side of a part exchange), so `fields` is not a flat map of
 * scalars. The checklist below therefore reports on the SHAPE of what was
 * heard — who it is for, what they are buying, what they are selling — rather
 * than on individual form fields, which is also what a rep would check first.
 */

import { requestTypes } from "../constants";

/** The flow's own caps: ConfirmRequest disables its add buttons at 3 and 3. */
export const MAX_PER_SIDE = 3;

export const INTENTS = {
  ORDER: "order",
  SELL: "sell",
  PX: "px",
};

/**
 * What each intent is CALLED, and what it creates. One place, because the
 * label appears on the type control, in the create button and in the
 * disagreement warning — and those three disagreeing would be its own bug.
 */
export const INTENT_LABELS = {
  [INTENTS.ORDER]: "Sourcing enquiry",
  [INTENTS.SELL]: "Sell enquiry",
  [INTENTS.PX]: "Part exchange",
};

export const INTENT_ACTION = {
  [INTENTS.ORDER]: "Create sourcing enquiry",
  [INTENTS.SELL]: "Create sell enquiry",
  [INTENTS.PX]: "Create part exchange",
};

/** The canonical request type each intent writes. PX starts as an order. */
export const INTENT_REQUEST_TYPE = {
  [INTENTS.ORDER]: requestTypes.ORDER,
  [INTENTS.SELL]: requestTypes.SELL,
  [INTENTS.PX]: requestTypes.ORDER,
};

const count = (list) => (Array.isArray(list) ? list.length : 0);

/**
 * `fields` here is the normalised result (see services/requestDictation.js),
 * not the listing schema's per-key confidence map — the overlay only ever
 * passes it through to these functions, so the shape is ours to choose.
 */
const CHECKLIST = [
  {
    key: "client",
    label: "Who it is for",
    status: (r) =>
      r?.clientName
        ? { done: true, text: r.clientName }
        : { done: false, text: "still needed" },
  },
  {
    key: "intent",
    label: "Buying, selling, or both",
    status: (r) =>
      r?.intent
        ? { done: true, text: INTENT_LABELS[r.intent] ?? r.intent }
        : { done: false, text: "still needed" },
  },
  {
    key: "orders",
    label: "The watch they want",
    status: (r) => {
      const n = count(r?.orders);
      return n
        ? { done: true, text: n === 1 ? "1 watch" : `${n} watches` }
        : { done: false, text: "nothing heard" };
    },
  },
  {
    key: "sells",
    label: "The watch they are selling",
    status: (r) => {
      const n = count(r?.sells);
      return n
        ? { done: true, text: n === 1 ? "1 watch" : `${n} watches` }
        : { done: false, text: "nothing heard" };
    },
  },
];

export const requestDictationSchema = {
  title: "Instant Request Creation with voice",
  exampleLabel: "Example",
  hint:
    "Speak naturally. You can review every detail before the request is created.",

  // Covers all three intents in one sentence a dealer would actually say. Shown
  // fully populated with the swappable parts marked, for the same reason the
  // listing one is: a filled sentence is understood at a glance where a row of
  // empty slots has to be decoded first.
  template: [
    { value: "John Doe", slot: "client" },
    " wants a ",
    { value: "2023", slot: "year" },
    " ",
    { value: "Rolex Daytona", slot: "product" },
    " ref ",
    { value: "116500LN", slot: "reference" },
    ", part-exchanging his ",
    { value: "2021 Submariner", slot: "trade_in" },
    ", ",
    { value: "unworn", slot: "condition" },
    " with ",
    { value: "box and papers", slot: "delivery_set" },
    ".",
  ],

  checklist: CHECKLIST.map(({ key, label }) => ({ key, label })),

  // The overlay hands `{ fields }`; for requests that IS the normalised result.
  checklistStatus(item, { fields }) {
    const row = CHECKLIST.find((c) => c.key === item.key);
    return row ? row.status(fields) : { done: false, text: "" };
  },

  // No "also heard" block: everything a request captures is shown on a card, so
  // there is nothing captured-but-not-displayed to confess to.
  extraLabel: () => null,
  extrasNote: () => null,
  formatValue: () => "",

  /**
   * Money is read back in words before anything is created, the same guard the
   * listing sheet has — a misheard price is the most expensive error available,
   * and a request carries up to three money fields per item where a listing had
   * one.
   */
  heardMoney(result) {
    const lines = [];

    (result?.orders ?? []).forEach((item, i) => {
      const label = `Budget ${(result.orders.length > 1 ? `${i + 1} ` : "")}`.trim();
      if (item.minBudget != null && item.maxBudget != null) {
        lines.push(`${label}: ${item.minBudget} to ${item.maxBudget}`);
      } else if (item.maxBudget != null) {
        lines.push(`${label}: up to ${item.maxBudget}`);
      } else if (item.minBudget != null) {
        lines.push(`${label}: from ${item.minBudget}`);
      }
    });

    (result?.sells ?? []).forEach((item, i) => {
      if (item.priceExpectation == null) return;
      const label = `Asking ${(result.sells.length > 1 ? `${i + 1} ` : "")}`.trim();
      lines.push(`${label}: ${item.priceExpectation}`);
    });

    return lines;
  },

  selectionNote:
    "Nothing is created until you press the button below. Each watch needs its product confirmed first.",

  emptyHint:
    "Nothing in the catalogue matched closely enough to offer. Here's what we heard — search for the watch manually and the rest carries over.",
};

export default requestDictationSchema;
